import React, { useRef, useState } from 'react';

import storage from '@dashboard/services/storage';

const STORAGE_KEY = 'editor-form-width';
const DEFAULT_WIDTH = 544;
const MIN_WIDTH = 360;
const MAX_WIDTH = 760;

const clamp = width => Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, width));

function readWidth() {
  try {
    const stored = Number(storage.local.getItem(STORAGE_KEY));
    return stored ? clamp(stored) : DEFAULT_WIDTH;
  } catch {
    return DEFAULT_WIDTH;
  }
}

function saveWidth(width) {
  try {
    storage.local.setItem(STORAGE_KEY, String(width));
  } catch {
    // storage can be unavailable; the width then lasts until the next load
  }
}

/** The editor's form column width, remembered per browser. */
export function useFormColumnWidth() {
  const [width, setWidth] = useState(readWidth);
  return [width, setWidth];
}

// Sits on the line between the form and the preview. Dragging left gives the preview more room;
// a double click goes back to the default.
export default function PreviewResizeHandle({ width, onChange }) {
  const drag = useRef(null);
  const [isDragging, setDragging] = useState(false);

  const onPointerDown = event => {
    // Pointer capture keeps the drag going over the preview's iframe.
    event.currentTarget.setPointerCapture(event.pointerId);
    drag.current = { startX: event.clientX, startWidth: width, width };
    setDragging(true);
    document.body.style.userSelect = 'none';
  };

  const onPointerMove = event => {
    if (!drag.current) return;
    drag.current.width = clamp(drag.current.startWidth + event.clientX - drag.current.startX);
    onChange(drag.current.width);
  };

  const endDrag = event => {
    if (!drag.current) return;
    saveWidth(drag.current.width);
    drag.current = null;
    setDragging(false);
    document.body.style.userSelect = '';
    event.currentTarget.releasePointerCapture?.(event.pointerId);
  };

  const reset = () => {
    onChange(DEFAULT_WIDTH);
    saveWidth(DEFAULT_WIDTH);
  };

  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label="Resize preview"
      title="Drag to resize the preview"
      className="group/resize absolute inset-y-0 left-0 z-10 hidden w-3 -translate-x-1/2 cursor-col-resize touch-none lg:block"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onDoubleClick={reset}
    >
      <div
        className={`absolute inset-y-0 left-1/2 w-px -translate-x-1/2 transition-colors ${
          isDragging
            ? 'bg-gray-300 dark:bg-accent'
            : 'bg-transparent group-hover/resize:bg-gray-200 dark:group-hover/resize:bg-accent'
        }`}
      />
      <div className="absolute top-1/2 left-1/2 h-8 w-1 -translate-x-1/2 -translate-y-1/2 rounded-full border border-gray-200 dark:border-border bg-white dark:bg-background" />
    </div>
  );
}
